import React, { useState } from 'react';
import {
  Grid,
  Typography,
  InputAdornment,
  OutlinedInput,
  InputLabel,
  FormControl,
  IconButton,
} from '@mui/material';
import { useApiGet, useApiSend } from '../../utils/httpClient';
import {
  getConversationMessages,
  createMessage,
} from '../../endpoints/messages';
import { Send } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
var utc = require('dayjs/plugin/utc');

dayjs.extend(utc);

const ConfirmationDialog = ({
  userId,
  senderId,
  content,
  timestamp,
  senderName,
}) => {
  // Dialog implementation if the userId matches the senderId it goes on the right side
  // and has primary color otherwise it goes on the left side and has secondary color.
  return (
    <Grid
      container
      justifyContent={userId === senderId ? 'flex-end' : 'flex-start'}
    >
      <Grid item>
        <Typography
          variant="body2"
          color={userId === senderId ? 'primary.main' : 'secondary.main'}
        >
          {content}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {dayjs.utc(timestamp).local().format('M/DD/YYYY h:mm A')} -{' '}
          {senderName}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default function ConversationDetail() {
  const { id } = useParams();
  const userId = localStorage.getItem('user_id');
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);
  const [newMessage, setNewMessage] = useState('');

  const {
    data: conversationMessage,
    isFetching,
    refetch,
  } = useApiGet(
    ['conversations', id],
    getConversationMessages,
    {
      enabled: userId !== null,
      retry: 1,
      refetchInterval: 60 * 1000, // The data will be refetched every 1 minutes
    },
    { id }
  );

  const { mutate: sendMessage } = useApiSend(
    createMessage,
    (data) => {
      setIsSaving(false);
      setNewMessage('');
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const handleSendMessage = () => {
    if (newMessage.trim() === '') {
      return;
    }
    setIsSaving(true);
    // Here you would call the API to send the message
    // For now, we will just simulate a successful send
    sendMessage({
      conversationId: id,
      message: newMessage,
    });
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  if (isFetching && !conversationMessage) {
    return (
      <Grid container spacing={2}>
        <Typography>Loading...</Typography>
      </Grid>
    );
  }

  if (!conversationMessage || conversationMessage.length === 0) {
    return (
      <Grid container spacing={2}>
        <Typography>No conversations found.</Typography>
      </Grid>
    );
  }
  const handleBacktoList = () => {
    window.location.href = '/conversations';
  };

  const orderedMessages = conversationMessage.messages.sort((a, b) => {
    return new Date(b.timestamp) - new Date(a.timestamp);
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={1}>
        <IconButton onClick={() => handleBacktoList()}>
          <Typography variant="body1">Back</Typography>
        </IconButton>
      </Grid>
      <Grid item xs={10} textAlign={'center'} padding={2}>
        <Typography variant="h5">{conversationMessage.title}</Typography>
      </Grid>
      <Grid item xs={1} />
      <Grid item xs={12} padding={2}>
        <FormControl fullWidth variant="outlined" size="small">
          <InputLabel htmlFor="new-message">New Message</InputLabel>
          <OutlinedInput
            id="new-message"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSendMessage}
                  style={{ cursor: 'pointer' }}
                  disabled={isSaving}
                >
                  <Send color={'primary'} />
                </IconButton>
              </InputAdornment>
            }
            label="New Message"
            multiline
            onKeyDown={handleKeyPress}
            error={!!errorSaving}
            helperText={errorSaving ? errorSaving : ''}
          />
        </FormControl>
      </Grid>
      {conversationMessage &&
        orderedMessages.map((message) => (
          <Grid item xs={12} key={message.id}>
            <ConfirmationDialog
              userId={userId}
              senderId={message.sender_id}
              content={message.content}
              timestamp={message.timestamp}
              senderName={message.sender_name}
            />
          </Grid>
        ))}
    </Grid>
  );
}
